import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Box,
  Typography,
  Divider,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  alpha,
  CircularProgress,
  Tooltip,
  IconButton,
  Stack,
  Breadcrumbs,
  Link,
  Avatar,
  Fade
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  AutorenewOutlined as RetryIcon,
  Error as ErrorIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
  School as SchoolIcon,
  Category as CategoryIcon,
  Title as TitleIcon,
  FormatListNumbered as ListIcon,
  TextFields as TextIcon,
  Help as HelpIcon,
  PlaylistAddCheck as QuestionsIcon
} from '@mui/icons-material';

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';

import { useTheme } from '@mui/material/styles';

import { useReadingPracticeStatus } from '../../hooks/useReadingPracticeStatus';
import { useRetryReadingPractice } from '../../hooks/useRetryReadingPractice';
import { categories } from '../../constants/categories';

const RequestDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();

  const {
    data: request,
    isLoading: loading,
    error,
    fetchStatus: fetchRequest
  } = useReadingPracticeStatus(id);

  const {
    retryPractice: retryRequest,
    isLoading: retrying
  } = useRetryReadingPractice();

  const isRetryable = request?.status === 'GENERATION_FAILED' || request?.status === 'DRAFTING_FAILED';
  
  // Refresh the request data
  const handleRefresh = () => {
    fetchRequest();
  };
  
  // Retry the failed request
  const handleRetry = async () => {
    try {
      await retryRequest(id);
    } catch (err) {
      // Error is already handled in the hook
    }
  };

  // Auto-polling for request status when pending/generating/drafting
  useEffect(() => {
    if (!request) return;
    
    const isProcessing = 
      request.status === 'PENDING' || 
      request.status === 'GENERATING_CONTENT' || 
      request.status === 'DRAFTING';
      
    if (isProcessing) {
      const intervalId = setInterval(() => {
        fetchRequest();
      }, 5000);
      
      return () => clearInterval(intervalId);
    }
  }, [request, fetchRequest]);
  
  // Go back to the requests list
  const handleBack = () => {
    navigate('/requests');
  };
  
  // Get status chip and icon properties based on status
  const getStatusChipProps = (status) => {
    const statusMap = {
      'PENDING': {
        color: 'default',
        label: 'Pending',
        icon: <CircularProgress size={12} color="inherit" />,
        bg: alpha(theme.palette.grey[500], 0.2),
        textColor: theme.palette.text.secondary,
        timelineColor: 'grey'
      },
      'GENERATING_CONTENT': {
        color: 'primary',
        label: 'Generating',
        icon: <CircularProgress size={12} color="inherit" />,
        bg: alpha(theme.palette.primary.main, 0.1),
        textColor: theme.palette.primary.main,
        timelineColor: 'primary'
      },
      'GENERATED': {
        color: 'info',
        label: 'Generated',
        icon: <InfoIcon fontSize="small" />,
        bg: alpha(theme.palette.info.main, 0.1),
        textColor: theme.palette.info.main,
        timelineColor: 'info'
      },
      'GENERATION_FAILED': {
        color: 'error',
        label: 'Generation Failed',
        icon: <ErrorIcon fontSize="small" />,
        bg: alpha(theme.palette.error.main, 0.1),
        textColor: theme.palette.error.main,
        timelineColor: 'error'
      },
      'DRAFTING': {
        color: 'primary',
        label: 'Drafting',
        icon: <CircularProgress size={12} color="inherit" />,
        bg: alpha(theme.palette.primary.main, 0.1),
        textColor: theme.palette.primary.main,
        timelineColor: 'primary'
      },
      'DRAFTING_FAILED': {
        color: 'error',
        label: 'Drafting Failed',
        icon: <ErrorIcon fontSize="small" />,
        bg: alpha(theme.palette.error.main, 0.1),
        textColor: theme.palette.error.main,
        timelineColor: 'error'
      },
      'COMPLETED': {
        color: 'success',
        label: 'Completed',
        icon: <CheckIcon fontSize="small" />,
        bg: alpha(theme.palette.success.main, 0.1),
        textColor: theme.palette.success.main,
        timelineColor: 'success'
      }
    };

    return statusMap[status] || {
      color: 'default',
      label: status,
      bg: alpha(theme.palette.grey[500], 0.2),
      textColor: theme.palette.text.primary,
      timelineColor: 'grey'
    };
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Format duration for display
  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };
  
  // Render status timeline events
  const renderStatusTimeline = (events) => {
    if (!events || events.length === 0) {
      // For PENDING status with no events, show a waiting message
      if (request?.status === 'PENDING') {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={30} sx={{ mb: 2 }} />
            <Typography variant="body2" color="textSecondary" align="center">
              Request is pending processing.
            </Typography>
            <Typography variant="caption" color="textSecondary" align="center" sx={{ mt: 1 }}>
              Status events will appear here once processing begins.
            </Typography>
          </Box>
        );
      }
      
      // For other statuses with no events
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <TimelineIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.5), mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            No status events available
          </Typography>
        </Box>
      );
    }
    
    return (
      <Timeline position="right" sx={{ px: 0 }}>
        {events.map((event, index) => {
          const { timelineColor, label, icon } = getStatusChipProps(event.newStatus);
          return (
            <TimelineItem key={index}>
              <TimelineOppositeContent sx={{ maxWidth: '30%', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(event.timestamp)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatDuration(event.timeTakenMs)}
                </Typography>
              </TimelineOppositeContent>
              
              <TimelineSeparator>
                <TimelineDot color={timelineColor} variant={index === 0 ? "filled" : "outlined"}>
                  {icon}
                </TimelineDot>
                {index < events.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              
              <TimelineContent>
                <Typography variant="body2" fontWeight="500">
                  {label}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {event.oldStatus ? `From: ${getStatusChipProps(event.oldStatus).label}` : 'Initial request'}
                </Typography>
                {event.reason && (
                  <Typography 
                    variant="caption" 
                    color="error" 
                    display="block"
                    sx={{ 
                      mt: 0.5,
                      p: 1, 
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                      borderRadius: 1,
                      borderLeft: `3px solid ${alpha(theme.palette.error.main, 0.5)}`
                    }}
                  >
                    {event.reason}
                  </Typography>
                )}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    );
  };

  // Info field with icon
  const InfoField = ({ icon, label, value, xs = 12, sm = 6 }) => (
    <Grid item xs={xs} sm={sm}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateX(4px)'
        }
      }}>
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            mr: 1.5, 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight="500">
            {value || 'N/A'}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs 
        separator={<ChevronRightIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="/"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="/requests"
          onClick={(e) => {
            e.preventDefault();
            handleBack();
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ListIcon sx={{ mr: 0.5 }} fontSize="small" />
          Requests
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 0.5 }} fontSize="small" />
          Request Details
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Card 
        elevation={1} 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}
      >
        <Box sx={{ 
          p: 3, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.1)
        }}>
          <Box>
            <Typography variant="h5" fontWeight="600">
              Reading Practice Request
            </Typography>
            {request && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                ID: {request.id}
              </Typography>
            )}
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title="Back to requests list">
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                size="small"
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none'
                }}
              >
                Back
              </Button>
            </Tooltip>
            
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                color="primary"
                sx={{ borderRadius: '8px' }}
              >
                {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
            
            {isRetryable && (
              <Tooltip title="Retry failed request">
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<RetryIcon />}
                  onClick={handleRetry}
                  disabled={retrying}
                  sx={{ 
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2
                  }}
                >
                  Retry
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Box>
        
          {/* Current Status Banner */}
        {request && (
          <Fade in={true}>
            <Box sx={{ 
              px: 3, 
              py: 2,
              backgroundColor: (theme) => {
                const statusProps = getStatusChipProps(request.status);
                const baseColor = statusProps.color === 'default' ? 
                  theme.palette.grey[500] : 
                  theme.palette[statusProps.color]?.main || theme.palette.grey[500];
                return alpha(baseColor, request.status.includes('FAILED') ? 0.1 : 0.05);
              },
              borderBottom: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.1)
            }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <Avatar 
                    sx={{ 
                      bgcolor: (theme) => {
                        const statusProps = getStatusChipProps(request.status);
                        const baseColor = statusProps.color === 'default' ? 
                          theme.palette.grey[500] : 
                          theme.palette[statusProps.color]?.main || theme.palette.grey[500];
                        return alpha(baseColor, 0.2);
                      },
                      color: (theme) => {
                        const statusProps = getStatusChipProps(request.status);
                        return statusProps.color === 'default' ? 
                          '#ffffff' : 
                          theme.palette[statusProps.color]?.main || '#ffffff';
                      }
                    }}
                  >
                    {getStatusChipProps(request.status).icon}
                  </Avatar>
                </Grid>
                
                <Grid item xs>
                  <Typography variant="subtitle1" fontWeight={500}>
                    Current Status: {getStatusChipProps(request.status).label}
                  </Typography>
                  
                  {request.status === 'PENDING' && (
                    <Typography variant="body2" color="textSecondary">
                      Your request is waiting to be processed. This may take a few moments.
                    </Typography>
                  )}
                  
                  {request.failureReason && (
                    <Typography 
                      variant="body2" 
                      color="error" 
                      sx={{ 
                        mt: 1,
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.error?.main || '#f44336', 0.05),
                        borderRadius: 1,
                        borderLeft: `4px solid ${alpha(theme.palette.error?.main || '#f44336', 0.5)}`
                      }}
                    >
                      <ErrorIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                      {request.failureReason}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Fade>
        )}
      </Card>
      
      {/* Loading State */}
      {loading && !request && (
        <Card sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          p: 8,
          borderRadius: 2
        }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" color="textSecondary">
            Loading request details...
          </Typography>
        </Card>
      )}
      
      {/* Error State */}
      {error && (
        <Card 
          elevation={1}
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.05),
            border: '1px solid',
            borderColor: alpha(theme.palette.error.main, 0.3),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <ErrorIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Request
          </Typography>
          <Typography color="error">{error}</Typography>
          <Button 
            variant="outlined" 
            color="error" 
            sx={{ mt: 2 }}
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Try Again
          </Button>
        </Card>
      )}
      
      {/* Request Details */}
      {request && (
        <Grid container spacing={3}>
          {/* Request Information */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                height: '100%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }}
            >
              <CardHeader 
                title="Request Information" 
                titleTypographyProps={{ fontWeight: 600 }}
                sx={{ 
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.1)
                }}
              />
              
              <CardContent sx={{ p: 3 }}>
                <Fade in={true} style={{ transitionDelay: '100ms' }}>
                  <Grid container>
                    <InfoField 
                      icon={<SchoolIcon fontSize="small" />} 
                      label="Grade Level" 
                      value={request.gradeLevel} 
                    />
                    
                    <InfoField 
                      icon={<CategoryIcon fontSize="small" />} 
                      label="Category" 
                      value={categories[request.categoryId-1]} 
                    />
                    
                    <InfoField 
                      icon={<TitleIcon fontSize="small" />} 
                      label="Topic" 
                      value={request.topic}
                      xs={12} 
                    />
                    
                    <Box sx={{ width: '100%', my: 2 }}>
                      <Divider>
                        <Chip 
                          label="Content Configuration" 
                          size="small"
                          sx={{ 
                            fontWeight: 500,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }} 
                        />
                      </Divider>
                    </Box>
                    
                    <InfoField 
                      icon={<TextIcon fontSize="small" />} 
                      label="Paragraphs" 
                      value={request.numberOfParagraphs || 1} 
                    />
                    
                    <InfoField 
                      icon={<TextIcon fontSize="small" />} 
                      label="Words Per Paragraph" 
                      value={request.wordsPerParagraph || 100} 
                    />
                    
                    <InfoField 
                      icon={<HelpIcon fontSize="small" />} 
                      label="Decode Words" 
                      value={request.numberOfWordsForDecode || 10} 
                    />
                    
                    <InfoField 
                      icon={<QuestionsIcon fontSize="small" />} 
                      label="Comprehension Questions" 
                      value={request.numberOfQuestionsForComprehension || 4} 
                    />
                    
                    <InfoField 
                      icon={<ListIcon fontSize="small" />} 
                      label="Options Per Question" 
                      value={request.optionsPerQuestion || 4} 
                    />
                  </Grid>
                </Fade>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Status Timeline */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                height: '100%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }}
            >
              <CardHeader 
                title="Status History" 
                titleTypographyProps={{ fontWeight: 600 }}
                avatar={
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                    <TimelineIcon />
                  </Avatar>
                }
                sx={{ 
                  borderBottom: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.1)
                }}
              />
              
              <CardContent sx={{ p: 0, pb: '0 !important', maxHeight: 400, overflowY: 'auto' }}>
                <Fade in={true} style={{ transitionDelay: '200ms' }}>
                  <Box>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : (
                      renderStatusTimeline(request.events)
                    )}
                  </Box>
                </Fade>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Generated Content Section (placeholder) */}
          {request.status === 'COMPLETED' && (
            <Grid item xs={12}>
              <Card
                elevation={1}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <CardHeader 
                  title="Generated Content" 
                  titleTypographyProps={{ fontWeight: 600 }}
                  sx={{ 
                    borderBottom: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.1)
                  }}
                />
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Generated content would be displayed here...
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Footer Actions */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 2,
                flexWrap: 'wrap',
                gap: 2 
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none'
                }}
              >
                Back to Requests
              </Button>
              
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{ 
                    borderRadius: '8px',
                    textTransform: 'none'
                  }}
                >
                  Refresh
                </Button>
                
                {isRetryable && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<RetryIcon />}
                    onClick={handleRetry}
                    disabled={retrying}
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: 2
                    }}
                  >
                    Retry Request
                  </Button>
                )}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default RequestDetail;